import { useEffect, useRef, useState } from "react";
import { useLocation, globalHistory } from "@reach/router";
import { useFileUploadContext } from "./FileUploadContext";

/**
 * Hook that also prevents browser navigation (page close/refresh)
 * Combines both @reach/router and beforeunload blocking
 */
export function usePreventAllNavigationDuringUpload(
	customMessage = "Files are still uploading. Are you sure you want to leave this page (and cancel uploads)?"
) {
	const location = useLocation();
	const { cancelAllUploads, isUploading } = useFileUploadContext();
	const isBlockingRef = useRef(false);
	const [hasUserCancelled, setHasUserCancelled] = useState(false);

	// Block @reach/router navigation
	useEffect(() => {
		const unblock = globalHistory.listen(({ location: newLocation }) => {
			if (isUploading && !isBlockingRef.current) {
				const shouldBlock = !window.confirm(customMessage);

				if (shouldBlock) {
					isBlockingRef.current = true;
					globalHistory.navigate(location.pathname, { replace: true });
					setTimeout(() => {
						isBlockingRef.current = false;
					}, 0);
				} else {
					// user wants to leave, cancel any uploads
					cancelAllUploads();
					setHasUserCancelled(true);
				}
			}
		});

		return unblock;
	}, [isUploading, location.pathname, customMessage]);

	// Block browser navigation (page close/refresh)
	useEffect(() => {
		const handleBeforeUnload = (e: BeforeUnloadEvent) => {
			if (isUploading) {
				e.preventDefault();
				return (e.returnValue = "");
			}
		};

		window.addEventListener("beforeunload", handleBeforeUnload);
		return () => window.removeEventListener("beforeunload", handleBeforeUnload);
	}, [isUploading]);

	return { hasUserCancelled };
}
